import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Save, Upload, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { tplApi } from "@/lib/templateApi";
import ImageUpload from "@/components/ImageUpload";

export default function AboutEditor() {
  const { tenant, slug } = useOutletContext();
  const theme = tenant?.theme || {};
  const primary = theme.primary_color || "#1E3A5F";

  const [form, setForm] = useState({ headline: "", body: "", photo_url: "" });
  const [published, setPublished] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await tplApi.getAdminContent(slug);
      const m = res.modules?.about;
      if (m) {
        const src = m.draft || m.published;
        if (src) setForm(src.content_json);
        if (m.published) setPublished(m.published.content_json);
      }
    } catch {}
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg, type = "success", link = null) => { setToast({ msg, type, link }); setTimeout(() => setToast(null), 5000); };

  const save = async (action) => {
    setSaving(true);
    try {
      await tplApi.saveContent(slug, "about", form, action);
      flash(action === "publish" ? "Published!" : "Draft saved.", "success", action === "publish" ? `/t/${slug}` : null);
      if (action === "publish") setPublished({ ...form });
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(false); }
  };

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="about-editor">
      {/* Editor */}
      <div className="lg:w-[440px] shrink-0 border-r border-white/[0.06] overflow-auto">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-white">About Section</h2>
          <p className="text-slate-500 text-xs mt-0.5">Tell visitors who you are and what makes you special.</p>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-300 block mb-1">Headline</label>
            <Input value={form.headline} onChange={(e) => up("headline", e.target.value)}
              className="bg-[#1E293B] border-white/10 text-white h-10 rounded-lg focus:ring-orange-500" data-testid="about-field-headline" />
            <p className="text-[11px] text-slate-600 mt-1">A short title for the section (e.g. "Our Story").</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300 block mb-1">Body Text</label>
            <textarea value={form.body} onChange={(e) => up("body", e.target.value)} rows={8}
              className="w-full bg-[#1E293B] border border-white/10 text-white text-sm rounded-lg p-3 resize-none focus:ring-orange-500 focus:outline-none placeholder:text-slate-600"
              placeholder="Tell your story..." data-testid="about-field-body" />
            <p className="text-[11px] text-slate-600 mt-1">Use blank lines to separate paragraphs.</p>
          </div>
          <ImageUpload
            slug={slug}
            value={form.photo_url}
            onChange={(url) => up("photo_url", url)}
            label="Photo"
            help="Upload a photo of your space or team, or paste a URL below."
          />
          <div>
            <Input value={form.photo_url} onChange={(e) => up("photo_url", e.target.value)} type="url" placeholder="Or paste an image URL..."
              className="bg-[#1E293B] border-white/10 text-white h-9 rounded-lg text-xs focus:ring-orange-500" data-testid="about-field-photo" />
          </div>
        </div>
        <div className="p-6 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#0F172A]">
          <button onClick={() => save("draft")} disabled={saving} className="flex-1 bg-[#1E293B] border border-white/10 text-slate-300 hover:text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-draft-btn"><Save size={15} /> Save Draft</button>
          <button onClick={() => save("publish")} disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="publish-btn"><Upload size={15} /> Publish</button>
        </div>
      </div>

      {/* Preview */}
      <div className="flex-1 bg-[#0B1120] overflow-auto" data-testid="about-preview-panel">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-2"><Eye size={14} className="text-slate-500" /><span className="text-xs text-slate-500 font-medium">Live Preview</span></div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-white/[0.06] bg-slate-50" style={{ maxWidth: 800 }}>
            <div className="py-16 px-8 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              {form.photo_url && <div className="h-64 rounded-xl bg-cover bg-center" style={{ backgroundImage: `url(${form.photo_url})` }} />}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: `'${theme.font || "Inter"}', sans-serif` }}>{form.headline || "About Us"}</h2>
                <div className="w-10 h-0.5 rounded mb-4" style={{ background: primary }} />
                {(form.body || "").split("\n\n").map((p, i) => p.trim() && <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3">{p}</p>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium z-50 ${toast.type === "error" ? "bg-red-900/90 text-red-200 border border-red-800/50" : "bg-emerald-900/90 text-emerald-200 border border-emerald-800/50"}`}>{toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}{toast.msg}{toast.link && <a href={toast.link} target="_blank" rel="noopener noreferrer" className="underline ml-1">View site &rarr;</a>}</div>}
    </div>
  );
}
